import React from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

const EmptyText = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-slate);
`

const PostureRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`

const TrendingUpSvg = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z"/>
  </svg>
)

const PostureBadge = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: var(--vo-success);
  background: var(--vo-success-10);
  padding: 4px 12px;
  border-radius: 20px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
`

const PostureDesc = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-slate);
  margin: 0;
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 14px;
  margin-bottom: 36px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`

const Card = styled.div`
  background: white;
  border-radius: 12px;
  padding: 18px 20px;
  box-shadow: 0 1px 4px var(--vo-escuro-8);
  border: 1px solid var(--vo-cloud);
`

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 10px;
`

const CardName = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: var(--vo-ink);
  margin: 0;
`

const CardDesc = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-slate);
  margin: 0 0 10px;
  line-height: 1.55;
`

const getStatusStyle = (label) => {
  if (!label) return { color: 'var(--vo-slate)', bg: 'var(--vo-slate-10)' }
  const l = label.toLowerCase()
  if (l.includes('implement') || l.includes('operational') || l.includes('complete')) return { color: 'var(--vo-success)', bg: 'var(--vo-success-10)' }
  if (l.includes('progress') || l.includes('developing')) return { color: 'var(--vo-celeste)', bg: 'var(--vo-info-10)' }
  if (l.includes('plan') || l.includes('attention')) return { color: 'var(--vo-slate)', bg: 'var(--vo-slate-10)' }
  return { color: 'var(--vo-celeste)', bg: 'var(--vo-info-10)' }
}

const getMaturityStyle = (label) => {
  if (!label) return { color: 'var(--vo-slate)', bg: 'var(--vo-slate-10)' }
  const l = label.toLowerCase()
  if (l.includes('l3') || l.includes('defined') || l.includes('l4') || l.includes('l5') || l.includes('managed') || l.includes('optimized') || l.includes('gerenciado')) return { color: 'var(--vo-success)', bg: 'var(--vo-success-10)' }
  if (l.includes('l2') || l.includes('developing')) return { color: 'var(--vo-celeste)', bg: 'var(--vo-info-10)' }
  if (l.includes('l1') || l.includes('initial')) return { color: 'var(--vo-warning)', bg: 'var(--vo-warning-10)' }
  return { color: 'var(--vo-celeste)', bg: 'var(--vo-info-10)' }
}

const BadgePill = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${({ $color }) => $color};
  background: ${({ $bg }) => $bg};
  padding: 3px 10px;
  border-radius: 20px;
  white-space: nowrap;
  flex-shrink: 0;
`

export const GovernanceOverview = ({ categories = [], overallPosture }) => {
  const { t } = useTranslation('transparency')

  return (
    <div>
      {overallPosture && (
        <PostureRow>
          <PostureBadge>
            <TrendingUpSvg />
            {overallPosture.label || t('public.defaultPostureLabel')}
          </PostureBadge>
          {overallPosture.description && (
            <PostureDesc>{overallPosture.description}</PostureDesc>
          )}
        </PostureRow>
      )}

      {categories && categories.length > 0 ? (
        <Grid>
          {categories.map((category, index) => {
            const statusStyle = getStatusStyle(category.statusLabel)
            const maturityStyle = getMaturityStyle(category.maturityLabel)
            return (
              <Card key={category.id || index}>
                <CardHeader>
                  <CardName>{category.name}</CardName>
                  {category.statusLabel && (
                    <BadgePill $color={statusStyle.color} $bg={statusStyle.bg}>{category.statusLabel}</BadgePill>
                  )}
                </CardHeader>
                {category.description && (
                  <CardDesc>{category.description}</CardDesc>
                )}
                {category.maturityLabel && (
                  <BadgePill $color={maturityStyle.color} $bg={maturityStyle.bg}>{category.maturityLabel}</BadgePill>
                )}
              </Card>
            )
          })}
        </Grid>
      ) : (
        <EmptyText>{t('public.governanceUnavailable')}</EmptyText>
      )}
    </div>
  )
}

GovernanceOverview.propTypes = {
  categories: PropTypes.array,
  overallPosture: PropTypes.object,
}
